import { ChevronDown } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
} from '@design-system';
import {
  consumerQuoteCarrier,
  consumerQuotePremium,
  consumerQuoteTotalPremiums,
  isHybridConsumerQuote,
  paymentScheduleOfQuote,
  type ConsumerCoverageOption,
  type ConsumerQuoteProjection,
} from '@shared/consumerPlan';
import { getCarrierLogoSrc } from '@/util/carrierLogo';
import { GUARANTEE_CONDITIONS, OPTION_LABELS } from '../lib/copy';
import { approxMoney, money } from '../lib/format';
import { DetailList, Figure, PolicyMaximums, type DetailRow } from './figures';

interface QuoteOptionCardProps {
  option: ConsumerCoverageOption;
  projection: ConsumerQuoteProjection | undefined;
  careStartAge: number;
  yearsUntilCare: number;
  isHighlighted: boolean;
  isRecommended: boolean;
  // Shared across the cards so opening one card's details opens them all, side by side.
  areDetailsOpen: boolean;
  onDetailsOpenChange: (open: boolean) => void;
  onChoose: () => void;
  className?: string;
}

function premiumSuffix(quote: ConsumerCoverageOption['quote']): string | null {
  switch (paymentScheduleOfQuote(quote)) {
    case 'tenPay':
      return '/yr';
    case 'ongoing':
      return '/mo';
    default:
      return null;
  }
}

function premiumNote(option: ConsumerCoverageOption, yearsUntilCare: number) {
  switch (paymentScheduleOfQuote(option.quote)) {
    case 'tenPay':
      return `${money(consumerQuoteTotalPremiums(option.quote, yearsUntilCare))} total over 10 years`;
    case 'singlePay':
      return 'One payment';
    default:
      return 'Ongoing payments; may increase';
  }
}

function paysBeneficiaries(option: ConsumerCoverageOption, projection: ConsumerQuoteProjection): boolean {
  return isHybridConsumerQuote(option.quote) && projection.deathBenefitWithoutCare > 0;
}

function policyDetails(option: ConsumerCoverageOption, projection: ConsumerQuoteProjection): DetailRow[] {
  return [
    ...(paysBeneficiaries(option, projection)
      ? [
          { label: 'Guaranteed minimum death benefit', value: `≥ ${money(projection.guaranteedDeathBenefitAfterCare)}` },
          { label: 'Death benefit if no care', value: money(projection.deathBenefitWithoutCare) },
        ]
      : [{ label: 'Beneficiaries', value: 'None' }]),
    { label: 'Projected policy payout', value: approxMoney(projection.projectedPayout) },
    // The same ROI the options are ranked by, as the advisor's product details show it.
    ...(option.quote.formattedROI ? [{ label: 'Total ROI', value: option.quote.formattedROI }] : []),
  ];
}

/** One coverage option: the premium, what insurance pays and what's left, then a button to choose it. */
export function QuoteOptionCard({
  option,
  projection,
  careStartAge,
  yearsUntilCare,
  isHighlighted,
  isRecommended,
  onChoose,
  areDetailsOpen,
  onDetailsOpenChange,
  className,
}: QuoteOptionCardProps) {
  const { quote } = option;
  const carrier = consumerQuoteCarrier(quote);
  const suffix = premiumSuffix(quote);

  return (
    <Card
      data-testid="consumer-quote-option"
      data-highlighted={isHighlighted || undefined}
      className={cn(
        'rounded-xl transition-colors duration-150 ease-out',
        isHighlighted ? 'border-primary ring-primary ring-1' : 'hover:border-primary/30',
        className,
      )}
    >
      <CardContent className="flex h-full flex-col gap-5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-primary text-xs font-semibold uppercase tracking-wide">
            {OPTION_LABELS[option.label]}
          </span>
          {isRecommended && <Badge className="-my-1">Most Popular</Badge>}
        </div>

        <div className="flex flex-col items-start gap-2">
          <img src={getCarrierLogoSrc(carrier)} alt="" className="h-6 max-w-24 object-contain object-left" />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-950">{carrier}</span>
            <span className="text-muted-foreground text-xs">{quote.productName}</span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Figure
            label="Premium"
            value={
              <>
                {money(consumerQuotePremium(quote))}
                {suffix && <span className="text-muted-foreground text-sm font-semibold">{suffix}</span>}
              </>
            }
          />
          <p className="text-muted-foreground text-xs">{premiumNote(option, yearsUntilCare)}</p>
        </div>

        {projection && (
          <>
            <div className="border-t pt-5">
              <PolicyMaximums
                age={careStartAge}
                monthlyMax={projection.monthlyMaxAtCareStart}
                totalMax={projection.maxBenefitAtCareStart}
              />
            </div>

            <Collapsible className="group/details" open={areDetailsOpen} onOpenChange={onDetailsOpenChange}>
              <CollapsibleTrigger asChild>
                <Button variant="link" size="sm" className="px-0 has-[>svg]:px-0">
                  Policy details
                  <ChevronDown className="transition-transform duration-200 ease-out group-data-[state=open]/details:rotate-180" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="flex flex-col gap-3">
                <DetailList rows={policyDetails(option, projection)} />
                {paysBeneficiaries(option, projection) && (
                  <p className="text-muted-foreground text-xs">{GUARANTEE_CONDITIONS}</p>
                )}
              </CollapsibleContent>
            </Collapsible>
          </>
        )}

        <Button className="mt-auto w-full" variant={isHighlighted ? 'default' : 'outline'} onClick={onChoose}>
          Choose this option
        </Button>
      </CardContent>
    </Card>
  );
}
